import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AdminDataService,
  CompetitionPriorityItem,
  PriorityVideoItem,
} from '../../controller/admin-data.service';

@Component({
  selector: 'app-priority-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './priority-management.component.html',
  styleUrls: ['./priority-management.component.scss']
})
export class PriorityManagementComponent implements OnInit {
  public competitions: CompetitionPriorityItem[] = [];
  public videos: PriorityVideoItem[] = [];

  public searchQuery: string = '';
  public isLoadingCompetitions: boolean = false;
  public isSavingCompetitions: boolean = false;
  public isSearchingVideos: boolean = false;
  public isUpdatingVideos: boolean = false;
  public feedbackMessage: string = '';
  public hasSearchedVideos: boolean = false;

  private selectedVideoIds = new Set<string>();
  public selectedCompetitionIds = new Set<number>();

  constructor(private adminDataService: AdminDataService) {}

  ngOnInit(): void {
    this.loadCompetitions();
  }

  public loadCompetitions(): void {
    this.isLoadingCompetitions = true;
    this.adminDataService.getCompetitionsWithPriority().subscribe({
      next: (competitions) => {
        this.competitions = competitions;
        this.selectedCompetitionIds = new Set(
          competitions.filter(c => !!c.is_priority).map(c => c.id)
        );
        this.isLoadingCompetitions = false;
      },
      error: () => {
        this.feedbackMessage = 'No se pudieron cargar las competiciones.';
        this.isLoadingCompetitions = false;
      }
    });
  }

  public toggleCompetitionSelection(competitionId: number, checked: boolean): void {
    if (checked) {
      this.selectedCompetitionIds.add(competitionId);
    } else {
      this.selectedCompetitionIds.delete(competitionId);
    }
  }

  public saveCompetitionPriorities(): void {
    this.isSavingCompetitions = true;
    this.feedbackMessage = '';

    this.adminDataService.updateCompetitionPriorities(Array.from(this.selectedCompetitionIds)).subscribe({
      next: (result) => {
        this.competitions = result.competitions;
        this.selectedCompetitionIds = new Set(
          result.competitions.filter(c => !!c.is_priority).map(c => c.id)
        );
        this.feedbackMessage = 'Prioridades de competición actualizadas.';
        this.isSavingCompetitions = false;
      },
      error: () => {
        this.feedbackMessage = 'No se pudieron actualizar las prioridades de competición.';
        this.isSavingCompetitions = false;
      }
    });
  }

  public searchVideos(): void {
    this.feedbackMessage = '';
    this.isSearchingVideos = true;
    this.selectedVideoIds.clear();

    this.adminDataService.searchVideosForPriority(this.searchQuery.trim(), 50).subscribe({
      next: (videos) => {
        this.videos = videos;
        this.hasSearchedVideos = true;
        this.isSearchingVideos = false;
      },
      error: () => {
        this.feedbackMessage = 'No se pudo completar la búsqueda de vídeos.';
        this.videos = [];
        this.hasSearchedVideos = true;
        this.isSearchingVideos = false;
      }
    });
  }

  public toggleVideoSelection(videoId: string, checked: boolean): void {
    if (checked) {
      this.selectedVideoIds.add(videoId);
    } else {
      this.selectedVideoIds.delete(videoId);
    }
  }

  public isVideoSelected(videoId: string): boolean {
    return this.selectedVideoIds.has(videoId);
  }

  public get hasSelectedVideos(): boolean {
    return this.selectedVideoIds.size > 0;
  }

  public get selectedVideosCount(): number {
    return this.selectedVideoIds.size;
  }

  public applyManualPriority(prioritize: boolean): void {
    if (!this.hasSelectedVideos) {
      return;
    }

    this.isUpdatingVideos = true;
    const ids = Array.from(this.selectedVideoIds);

    this.adminDataService.updateManualVideoPriority(ids, prioritize).subscribe({
      next: (result) => {
        this.feedbackMessage = prioritize
          ? `Prioridad manual aplicada a ${result.updated_count} vídeo(s).`
          : `Prioridad manual retirada de ${result.updated_count} vídeo(s).`;
        this.selectedVideoIds.clear();
        this.searchVideos();
        this.isUpdatingVideos = false;
      },
      error: () => {
        this.feedbackMessage = prioritize
          ? 'No se pudo aplicar la prioridad manual.'
          : 'No se pudo retirar la prioridad manual.';
        this.isUpdatingVideos = false;
      }
    });
  }
}
